import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import Loader from '../../themed/loader';
import NoItems from '../../blocks/no-items';
import Table from '../../common/table';
import Input from '../../themed/input/input';
import Pagination from '../../themed/pagination';
import Select from '../../themed/select/select';
import EditRole from '../../forms/editRole';
import Modal from '../../themed/modal';
import SyncTime from '../../blocks/sync-time';

import * as utils from '../../../utils/utilities';
import * as models from '../../../utils/models';

import withRoles from '../../hoc/withRoles';

interface RolesProps {
	roles: models.Role[];
	rolesLastUpdated: number | null;
	rolesLoading: boolean;
	refreshRoles: () => void;
}

const Roles: React.FC<RolesProps> = ({
	roles,
	rolesLastUpdated,
	rolesLoading,
	refreshRoles,
}): JSX.Element => {
	const [sortColumn, setSortColumn] = React.useState(initialSortColumn);
	const [searchQuery, setSearchQuery] = React.useState('');
	const [pageSize, setPageSize] = React.useState(10);
	const [currentPage, setCurrentPage] = React.useState(1);
	const [selectedRole, setSelectedRole] = React.useState<models.Role | null>();

	const handlepageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = +e.target.value;
		const actualPageSize: number = pageSize === 0 ? 1 : pageSize;

		setPageSize(actualPageSize);
	};

	const handleSetSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
	};

	const tableColumns = useMemo((): models.Column[] => {
		const editRoleColumn: models.Column = {
			path: 'id',
			label: '',
			content: (r: models.Role) => (
				<Button
					icon="pencil-fill"
					onClick={() => setSelectedRole(r)}
					label=""
				/>
			),
		};

		const tableColumns: models.Column[] = [...columns, editRoleColumn];
		return tableColumns;
	}, []);

	const closeSelectedRole = useCallback((): void => {
		setSelectedRole(null);
	}, []);

	const paginatedData = useMemo(() => {
		const filtered = roles.filter((role) => utils.compareSimilarValues(_.get(role, 'name'), searchQuery));

		const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
		const paginated = utils.paginate(sorted, currentPage, pageSize);

		return { totalCount: filtered.length, paginated };
	}, [currentPage, pageSize, searchQuery, roles, sortColumn]);

	return (
		<React.Fragment>
			<Modal isVisible={!!selectedRole} onClose={closeSelectedRole}>
				<EditRole role={selectedRole} />
			</Modal>

			<h2 className="form-title">Manage Roles</h2>

			<div className="divider my-3"></div>

			<div className="d-flex justify-content-between mb-2">
				<Input
					name="search"
					value={searchQuery}
					onChange={handleSetSearch}
					placeholder="Enter to search"
				/>

				<SyncTime lastSynced={rolesLastUpdated} />
			</div>

			<Loader isVisible={rolesLoading} />

			<Conditional isVisible={roles.length > 0}>
				<Table
					columns={tableColumns}
					data={paginatedData.paginated}
					sortColumn={sortColumn}
					onSort={setSortColumn}
					isFetching={false}
				/>
			</Conditional>

			<NoItems isVisible={roles.length === 0} onRefresh={refreshRoles} />

			<div className="d-flex justify-content-between align-items-center mt-4">
				<Select
					name="page-size"
					placeholder={'Showing (' + pageSize + 'rows)'}
					options={utils.transformPageRange(20)}
					value={pageSize}
					onChange={handlepageSizeChange}
				/>

				<Pagination
					itemsCount={paginatedData.totalCount}
					pageSize={pageSize}
					currentPage={currentPage}
					onPageChange={(page: number) => setCurrentPage(page)}
				/>
			</div>
		</React.Fragment>
	);
};

const initialSortColumn: models.SortColumn = {
	path: 'name',
	order: 'asc',
};

const columns: models.Column[] = [
	{
		path: 'name',
		label: 'Role Name',
	},
	{
		path: 'description',
		label: 'Role Description',
	},
	{
		path: 'manageProjects',
		label: 'Manage Project',
		content: (r: models.Role) => <span>{r.manageProjects ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'manageUsers',
		label: 'Manage Users',
		content: (r: models.Role) => <span>{r.manageUsers ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'manageRoles',
		label: 'Manage Roles',
		content: (r: models.Role) => <span>{r.manageRoles ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'manageReports',
		label: 'Create Report',
		content: (r: models.Role) => <span>{r.manageReports ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'previewAllReports',
		label: 'Preview All Report',
		content: (r: models.Role) => <span>{r.previewAllReports ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'approveReports',
		label: 'Approve Report',
		content: (r: models.Role) => <span>{r.approveReports ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'reviewReports',
		label: 'Review Report',
		content: (r: models.Role) => <span>{r.reviewReports ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'viewReports',
		label: 'View All Reports',
		content: (r: models.Role) => <span>{r.viewReports ? 'Yes' : 'No'}</span>,
	},
	{
		path: 'viewAssociatedReports',
		label: 'View Associated Reports Only',
		content: (r: models.Role) => (
			<span>{r.viewAssociatedReports ? 'Yes' : 'No'}</span>
		),
	},
];

export default withRoles(Roles);
